import jsQR from 'jsqr';
import { Injectable } from '@angular/core';
import { Parse } from '@chemist2u/types-client/C2U/local-parse';
import { ICustomerMedicalProfile, TOrderPrescription, TOrderPrescriptionScriptItem } from '@chemist2u/types-client/C2U/Interfaces';
import { LatLngLiteral } from '@googlemaps/google-maps-services-js';
import { Camera, CameraResultType, CameraSource, Photo } from '@capacitor/camera';

export type TCombinedPrescriptionProfilesParam = TOrderPrescription[] | Omit<TOrderPrescription, "amount">[];
export type TCombinedPrescriptionProfilesResult = {
  profile: Parse.Object.ToJSON<ICustomerMedicalProfile> & Parse.JSONBaseAttributes,
  items: TOrderPrescriptionScriptItem[]
}[];

interface Schedule8Medication {
  genericName: string;
  brandNames: string[];
}


@Injectable({
  providedIn: 'root'
})
export class UtilService {

  private schedule8Medications: Schedule8Medication[] = [
    { genericName: "alfentanil", brandNames: ["Rapifen"] },
    { genericName: "alprazolam", brandNames: ["Kalma", "Alprax"] },
    { genericName: "amobarbital", brandNames: ["Amytal"] },
    { genericName: "amfetamine", brandNames: [] },
    { genericName: "amphetamine", brandNames: [] }, // Alternative spelling
    { genericName: "buprenorphine", brandNames: ["Norspan", "Suboxone", "Temgesic"] },
    { genericName: "butorphanol", brandNames: ["Torbugesic", "Dolorex", "Butorgesic"] },
    { genericName: "cannabis", brandNames: [] },
    { genericName: "cocaine", brandNames: [] },
    { genericName: "codeine", brandNames: ["Codeine Linctus", "Codeine Phos Tablets"] },
    { genericName: "dexamfetamine", brandNames: [] },
    { genericName: "dexamphetamine", brandNames: [] }, // Alternative spelling
    { genericName: "dextropropoxyphene", brandNames: [] },
    { genericName: "dihydrocodeine", brandNames: [] },
    { genericName: "diphenoxylate", brandNames: [] },
    { genericName: "dronabinol", brandNames: [] },
    { genericName: "fentanyl", brandNames: ["Actiq", "Denpax", "Durogesic", "Fenpatch"] },
    { genericName: "flunitrazepam", brandNames: ["Hypnodorm"] },
    { genericName: "hydromorphone", brandNames: ["Dilaudid", "Jurnista"] },
    { genericName: "ketamine", brandNames: ["Ketalar", "Ketamil", "Ketavet", "Ketamav"] },
    { genericName: "N, α dimethyl 3,4 (methylenedioxy)phenylethylamine", brandNames: [] }, // MDMA
    { genericName: "lisdexamfetamine", brandNames: ["Vyvanse"] },
    { genericName: "methadone", brandNames: ["Biodone Forte", "Physeptone", "Methadone"] },
    { genericName: "methylamfetamine", brandNames: [] },
    { genericName: "methylamphetamine", brandNames: [] }, // Alternative spelling
    { genericName: "methylphenidate", brandNames: ["Concerta", "Ritalin"] },
    { genericName: "morphine", brandNames: ["Kapanol", "MS Contin", "Ordine"] },
    { genericName: "nabilone", brandNames: [] },
    { genericName: "nabiximols", brandNames: ["Sativex"] },
    { genericName: "opium", brandNames: [] },
    { genericName: "oxycodone", brandNames: ["Endone", "Oxycontin", "Oxynorm", "Targin"] },
    { genericName: "pentobarbital", brandNames: [] },
    { genericName: "pentobarbitone", brandNames: [] }, // Alternative spelling
    { genericName: "pethidine", brandNames: [] },
    { genericName: "pholcodine", brandNames: [] },
    { genericName: "psilocybine", brandNames: [] },
    { genericName: "quinalbarbitone", brandNames: [] },
    { genericName: "remifentanil", brandNames: ["Ultiva"] },
    { genericName: "secobarbital", brandNames: [] },
    { genericName: "secobarbitone", brandNames: [] }, // Alternative spelling
    { genericName: "sodium oxybate", brandNames: [] },
    { genericName: "tapentadol", brandNames: ["Palexia"] },
    { genericName: "tetrahydrocannabinols", brandNames: [] },
    { genericName: "MDMA", brandNames: [] } // Common name for N, α dimethyl 3,4 (methylenedioxy)phenylethylamine
  ];

  public combinePrescriptionProfiles(prescriptions: TCombinedPrescriptionProfilesParam): TCombinedPrescriptionProfilesResult {
    if (prescriptions.length === 0) {
      return [];
    }

    const profileMap = new Map();

    for (const prescription of prescriptions) {
      const profile = prescription.prescriptionProfile;
      if (profile && !profileMap.has(profile.objectId)) {
        profileMap.set(profile.objectId, {
          profile,
          items: []
        });
      }
    }

    for (const prescription of prescriptions) {
      const items = prescription.items;
      const profileId = prescription.prescriptionProfile?.objectId;
      if (profileId) {
        profileMap.get(profileId).items.push(...items);
      }
    }

    for (const key of profileMap.keys()) {
      const profile = profileMap.get(key)!;
      if (profile.items.length == 0) {
        profileMap.delete(key);
      }
    }

    return Array.from(profileMap.values());
  }

  public calculateDistanceHaversine(
    point1: LatLngLiteral,
    point2: LatLngLiteral,
    unit: 'km' | 'miles' | 'meters' = 'km'
  ): number {
    const toRadians = (degrees: number): number => degrees * (Math.PI / 180);

    const R = {
      km: 6371,          // Earth's radius in kilometers
      miles: 3958.8,     // Earth's radius in miles
      meters: 6371000,   // Earth's radius in meters
    };

    const lat1 = toRadians(point1.lat);
    const lat2 = toRadians(point2.lat);
    const deltaLat = toRadians(point2.lat - point1.lat);
    const deltaLon = toRadians(point2.lng - point1.lng);

    const a =
      Math.sin(deltaLat / 2) ** 2 +
      Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLon / 2) ** 2;

    const c = 2 * Math.asin(Math.sqrt(a));

    const distance = R[unit] * c;

    return distance;
  }

  private extractQrFromImage(image: Photo): Promise<string> {
    return new Promise((resolve, reject) => {
      const imageElement = new Image();
      imageElement.crossOrigin = "anonymous";
      imageElement.src = `data:image/jpeg;base64,${image.base64String}`;

      imageElement.onload = () => {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) {
          return reject("Failed to get canvas context");
        }

        // Resize image if too large
        const maxSize = 500;
        const scaleFactor = Math.min(maxSize / imageElement.width, maxSize / imageElement.height, 1);
        canvas.width = imageElement.width * scaleFactor;
        canvas.height = imageElement.height * scaleFactor;

        // Apply image filters to enhance QR detection
        context.filter = "grayscale(100%) contrast(200%)";
        context.drawImage(imageElement, 0, 0, canvas.width, canvas.height);

        // Extract image data
        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height);

        if (code) {
          resolve(code.data);
        } else {
          reject("No QR code found in image!");
        }
      };

      imageElement.onerror = () => reject("Failed to load image");
    });
  }

  public async uploadQrImage(): Promise<string> {
    let image: Photo | undefined;
    try {
      image = await Camera.getPhoto({
        quality: 80,
        allowEditing: false,
        resultType: CameraResultType.Base64,
        saveToGallery: true,
        source: CameraSource.Photos
      });
    } catch (error) {
      throw new Error("No image selected!");
    }

    try {
      return await this.extractQrFromImage(image);
    } catch (error) {
      throw new Error("No QR code found in image!");
    }
  }

  public isSchedule8Medication(medicationName: string): boolean {
    if (!medicationName) {
      return false;
    }

    // Clean and normalize the medication name
    const normalizedMedName = medicationName
      .replace(/\*\*/g, '') // Remove asterisks
      .toUpperCase();

    // Split the medication name into parts (by spaces, plus signs, commas, slashes)
    const medNameParts = normalizedMedName
      .replace(/\+/g, ' ')
      .replace(/\//g, ' ')
      .replace(/,/g, ' ')
      .split(/\s+/)
      .filter(part => part.length > 2); // Filter out parts that are too short (like "mg", "mL")

    // Remove common descriptors, dose information, and device-related terms
    const commonTermsToRemove = [
      // Dosage and form related
      "TAB", "FORTE", "DUO", "TABLET", "CAPSULE", "CAP", "MG", "ML", "DOSE",
      // Device related
      "PEN", "DEVICE", "INHALER", "PUMP", "SPRAY", "PATCH",
      // Common descriptors
      "ORAL", "LIQUID", "SR", "CR", "XR", "EXTENDED", "RELEASE", "IMMEDIATE"
    ];

    const filteredParts = medNameParts.filter(part => {
      // Skip dosage information (e.g., 875mg, 125mg, 10, 1mg/dose)
      if (/^\d+$/.test(part) || /^\d+MG$/.test(part) || part.includes("MG/") || part.includes("/DOSE")) {
        return false;
      }

      // Skip common terms
      return !commonTermsToRemove.includes(part);
    });

    // We want more precise matching to avoid false positives
    return this.schedule8Medications.some(s8med => {
      // For generic name, use exact word matching to avoid substring false positives
      const normalizedGenericName = s8med.genericName.toUpperCase();

      // Check if any filtered part exactly matches the generic name or part of a compound name
      const genericNameMatch = filteredParts.some(part => {
        // Exact match
        if (part === normalizedGenericName) {
          return true;
        }

        // For compound medications (with multiple active ingredients)
        // e.g., "OXYCODONE + NALOXONE" should match "OXYCODONE"
        const compoundParts = normalizedGenericName.split(/[\s,+]+/);
        return compoundParts.some(compoundPart => compoundPart === part);
      });

      if (genericNameMatch) {
        return true;
      }

      // For brand names, also use exact matching
      return s8med.brandNames.some(brand => {
        const normalizedBrand = brand.toUpperCase();
        return filteredParts.includes(normalizedBrand);
      });
    });
  }

}